/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
/**
 * A codec that can encode and decode values of type V to and from strings.
 * @typeParam V The type of values that can be encoded and decoded.
 */
export interface Codec<V> {
  /**
   * Decodes a string value into a value of type V.
   * @param value The value to decode.
   * @returns The decoded value.
   */
  parse: (value: string) => V;
  /**
   * Encodes a value of type V into a string.
   * @param value The value to encode.
   * @returns The encoded value.
   */
  stringify: (value: V) => string;
}

/**
 * A codec that can encode and decode Date objects to and from strings.
 */
export const CODEC_DATE: Codec<Date> = {
  parse: (value) => new Date(value),
  stringify: (value) => value.toISOString(),
};

/**
 * A codec that can encode and decode Date objects to and from strings, but only the date part.
 */
export const CODEC_DATE_ONLY: Codec<Date> = {
  parse: (value) => new Date(value),
  stringify: (value) => value.toISOString().split("T")[0],
};

/**
 * A codec that can encode and decode numbers to and from strings.
 */
export const CODEC_NUMBER: Codec<number> = {
  parse: (value) => Number(value),
  stringify: (value) => String(value),
};

/**
 * A codec that can encode and decode boolean values to and from strings.
 */
export const CODE_BOOLEAN: Codec<boolean> = {
  parse: (value) => value === "true",
  stringify: (value) => String(value),
};

/**
 * A codec that can encode and decode JSON values to and from strings.
 */
export const CODEC_JSON: Codec<unknown> = {
  parse: (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  },
  stringify: (value) => JSON.stringify(value),
};

/**
 * A codec that can encode and decode JSON values to and from strings.
 * If the JSON value is invalid, parsing will fail.
 */
export const CODEC_JSON_STRICT: Codec<unknown> = {
  parse: (value) => JSON.parse(value),
  stringify: (value) => JSON.stringify(value),
};

/**
 * A codec that can encode and decode strings to and from strings.
 */
export const CODEC_STRING: Codec<string> = {
  parse: (value) => value,
  stringify: (value) => value,
};
